import { useEffect, useState } from 'react';
import { API_URL } from '../config';
import DataTable from 'react-data-table-component';
import { 
    Plus, Edit, Trash2, Users, Phone, Mail, MapPin, 
    Calendar, DollarSign, Building, Loader2, AlertCircle, X, UserPlus, Key, Eye, EyeOff, UserX
} from 'lucide-react';

/**
 * ColaboradoresPage - Employee management page
 * Allows CRUD operations on employees/colaboradores
 */
export default function ColaboradoresPage() {
    const [colaboradores, setColaboradores] = useState([]);
    const [roles, setRoles] = useState([]);
    const [sucursales, setSucursales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [passwordModalOpen, setPasswordModalOpen] = useState(false);
    const [selectedColabForPassword, setSelectedColabForPassword] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        tipo_usuario_id: '',
        rut: '',
        telefono: '',
        direccion: '',
        fecha_contratacion: '',
        salario_base: '',
        sucursal_id: ''
    });

    const token = localStorage.getItem('token');
    const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
    };

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [colabRes, rolesRes, sucRes] = await Promise.all([
                fetch(`${API_URL}/api/colaboradores`, { headers }),
                fetch(`${API_URL}/api/users/roles`, { headers }),
                fetch(`${API_URL}/api/colaboradores/sucursales`, { headers })
            ]);

            if (!colabRes.ok) throw new Error('Error al cargar colaboradores');

            setColaboradores(await colabRes.json());
            setRoles(await rolesRes.json());
            setSucursales(await sucRes.json());
        } catch (err) {
            console.error(err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const url = editingId 
            ? `${API_URL}/api/colaboradores/${editingId}`
            : `${API_URL}/api/colaboradores`;
        const method = editingId ? 'PUT' : 'POST';

        try {
            const res = await fetch(url, {
                method,
                headers,
                body: JSON.stringify(formData)
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Error al guardar');
            }

            setModalOpen(false);
            resetForm();
            fetchData();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleDeactivate = async (id) => {
        if (!confirm('¿Desactivar este colaborador? El usuario no podrá iniciar sesión pero sus datos se conservan.')) return;
        try {
            const res = await fetch(`${API_URL}/api/colaboradores/${id}`, { method: 'DELETE', headers });
            if (!res.ok) {
                const data = await res.json();
                alert(data.error || 'Error al desactivar');
                return;
            }
            fetchData();
        } catch (err) {
            alert('Error al desactivar colaborador');
        }
    };

    const handleHardDelete = async (id, name) => {
        if (!confirm(`¿ELIMINAR PERMANENTEMENTE al colaborador "${name}" y su cuenta de acceso?\n\nEsta acción NO se puede deshacer.`)) return;
        try {
            const res = await fetch(`${API_URL}/api/colaboradores/${id}/eliminar-permanente`, { method: 'DELETE', headers });
            if (!res.ok) {
                const data = await res.json();
                alert(data.error || 'Error al eliminar');
                return;
            }
            fetchData();
        } catch (err) {
            alert('Error al eliminar colaborador');
        }
    };

    const openPasswordModal = (colab) => {
        setSelectedColabForPassword(colab);
        setNewPassword('');
        setShowPassword(false);
        setPasswordModalOpen(true);
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        
        if (newPassword.length < 6) {
            alert('La contraseña debe tener al menos 6 caracteres');
            return;
        }

        try {
            const res = await fetch(`${API_URL}/api/colaboradores/${selectedColabForPassword.id}/reset-password`, {
                method: 'POST',
                headers,
                body: JSON.stringify({ newPassword })
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Error al resetear contraseña');
            }

            alert('Contraseña actualizada correctamente');
            setPasswordModalOpen(false);
            setSelectedColabForPassword(null);
            setNewPassword('');
        } catch (err) {
            alert(err.message);
        }
    };

    const generateRandomPassword = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
        let password = '';
        for (let i = 0; i < 8; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        setNewPassword(password);
        setShowPassword(true);
    };

    const openEdit = (colab) => {
        setFormData({
            name: colab.name || '',
            email: colab.email || '',
            password: '',
            tipo_usuario_id: colab.tipo_usuario_id || '',
            rut: colab.rut || '',
            telefono: colab.telefono || '',
            direccion: colab.direccion || '',
            fecha_contratacion: colab.fecha_contratacion?.split('T')[0] || '',
            salario_base: colab.salario_base || '',
            sucursal_id: colab.sucursal_id || '',
            estado: colab.estado || 'activo'
        });
        setEditingId(colab.id);
        setModalOpen(true);
    };

    const resetForm = () => {
        setFormData({
            name: '', email: '', password: '', tipo_usuario_id: '',
            rut: '', telefono: '', direccion: '', fecha_contratacion: '',
            salario_base: '', sucursal_id: ''
        });
        setEditingId(null);
    };

    const formatCurrency = (amount) => {
        if (!amount) return '-';
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const columns = [
        {
            name: 'Colaborador',
            selector: row => row.name,
            sortable: true,
            cell: row => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0' }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '0.875rem'
                    }}>
                        {row.name?.charAt(0).toUpperCase()}
                    </div>
                    <div>
                        <div style={{ fontWeight: '600', color: 'var(--text-main)' }}>{row.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{row.email}</div>
                    </div>
                </div>
            ),
            grow: 2
        },
        {
            name: 'RUT',
            selector: row => row.rut || '-',
            sortable: true,
        },
        {
            name: 'Teléfono',
            selector: row => row.telefono || '-',
            cell: row => row.telefono ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Phone size={14} style={{ color: 'var(--text-secondary)' }} />
                    {row.telefono}
                </span>
            ) : '-'
        },
        {
            name: 'Rol',
            selector: row => row.rol_nombre,
            sortable: true,
            cell: row => (
                <span style={{
                    background: 'rgba(79, 70, 229, 0.1)',
                    color: 'var(--primary-color)',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: '600'
                }}>
                    {row.rol_nombre || 'Sin rol'}
                </span>
            )
        },
        {
            name: 'Sucursal',
            selector: row => row.sucursal_nombre || '-',
            sortable: true,
        },
        {
            name: 'Estado',
            selector: row => row.estado,
            sortable: true,
            cell: row => (
                <span style={{
                    background: row.estado === 'activo' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                    color: row.estado === 'activo' ? 'var(--secondary-color)' : 'var(--danger-color)',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '9999px',
                    fontSize: '0.75rem',
                    fontWeight: '600',
                    textTransform: 'capitalize'
                }}>
                    {row.estado}
                </span>
            )
        },
        {
            name: 'Acciones',
            cell: row => (
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button 
                        onClick={() => openEdit(row)}
                        title="Editar"
                        style={{
                            background: 'rgba(79, 70, 229, 0.1)',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.5rem',
                            cursor: 'pointer',
                            color: 'var(--primary-color)'
                        }}
                    >
                        <Edit size={16} />
                    </button>
                    <button 
                        onClick={() => openPasswordModal(row)}
                        title="Cambiar contraseña"
                        style={{
                            background: 'rgba(245, 158, 11, 0.1)',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.5rem',
                            cursor: 'pointer',
                            color: '#f59e0b'
                        }}
                    >
                        <Key size={16} />
                    </button>
                    <button 
                        onClick={() => handleDeactivate(row.id)}
                        title="Desactivar"
                        style={{
                            background: 'rgba(156, 163, 175, 0.2)',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.5rem',
                            cursor: 'pointer',
                            color: '#6b7280'
                        }}
                    >
                        <Trash2 size={16} />
                    </button>
                    <button 
                        onClick={() => handleHardDelete(row.id, row.name)}
                        title="Eliminar permanentemente (usuario + cuenta)"
                        style={{
                            background: 'rgba(239, 68, 68, 0.15)',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.5rem',
                            cursor: 'pointer',
                            color: 'var(--danger-color)'
                        }}
                    >
                        <UserX size={16} />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            button: true,
            width: '180px'
        }
    ];

    if (loading) {
        return (
            <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px' }}>
                <Loader2 size={48} className="spinning" style={{ color: 'var(--primary-color)' }} />
            </div>
        );
    }

    return (
        <div className="page">
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <div>
                    <h1 style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                        <Users size={28} />
                        Colaboradores
                    </h1>
                    <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                        Gestión del personal de la empresa
                    </p>
                </div>
                <button 
                    onClick={() => { resetForm(); setModalOpen(true); }}
                    className="btn-primary"
                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                >
                    <UserPlus size={18} /> Nuevo Colaborador
                </button>
            </div>

            {/* Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                <div className="report-card" style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--primary-color)' }}>
                        {colaboradores.filter(c => c.estado === 'activo').length}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Activos</div>
                </div>
                <div className="report-card" style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--secondary-color)' }}>
                        {colaboradores.length}
                    </div>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>Total</div>
                </div>
            </div>

            {/* Table */}
            <div className="report-card" style={{ padding: 0, overflow: 'hidden' }}>
                <DataTable
                    columns={columns}
                    data={colaboradores}
                    pagination
                    paginationComponentOptions={{
                        rowsPerPageText: 'Filas por página:',
                        rangeSeparatorText: 'de'
                    }}
                    highlightOnHover
                    responsive
                    noDataComponent={
                        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                            <Users size={48} style={{ opacity: 0.3, marginBottom: '1rem' }} />
                            <p>No hay colaboradores registrados</p>
                        </div>
                    }
                    customStyles={{
                        headRow: {
                            style: {
                                backgroundColor: '#f9fafb',
                                borderBottom: '1px solid var(--border-color)',
                                fontWeight: '600',
                                color: 'var(--text-secondary)',
                                textTransform: 'uppercase',
                                fontSize: '0.75rem'
                            }
                        },
                        cells: {
                            style: {
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            }
                        }
                    }}
                />
            </div>

            {/* Modal */}
            {modalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '600px' }}>
                        <button 
                            className="modal-close" 
                            onClick={() => { setModalOpen(false); resetForm(); }}
                        >
                            <X size={20} />
                        </button>
                        
                        <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <UserPlus size={24} />
                            {editingId ? 'Editar Colaborador' : 'Nuevo Colaborador'}
                        </h2>

                        <form onSubmit={handleSubmit}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                {/* Personal Info */}
                                <div className="form-group">
                                    <label>Nombre Completo *</label>
                                    <input
                                        required
                                        value={formData.name}
                                        onChange={e => setFormData({...formData, name: e.target.value})}
                                        placeholder="Juan Pérez"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>RUT</label>
                                    <input
                                        value={formData.rut}
                                        onChange={e => setFormData({...formData, rut: e.target.value})}
                                        placeholder="12.345.678-9"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Email *</label>
                                    <input
                                        required
                                        type="email"
                                        value={formData.email}
                                        onChange={e => setFormData({...formData, email: e.target.value})}
                                        placeholder="juan@empresa.cl"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Teléfono</label>
                                    <input
                                        value={formData.telefono}
                                        onChange={e => setFormData({...formData, telefono: e.target.value})}
                                        placeholder="+56 9 1234 5678"
                                    />
                                </div>

                                {!editingId && (
                                    <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                        <label>Contraseña *</label>
                                        <input
                                            required
                                            type="password"
                                            value={formData.password}
                                            onChange={e => setFormData({...formData, password: e.target.value})}
                                            placeholder="••••••••"
                                        />
                                    </div>
                                )}

                                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                                    <label>Dirección</label>
                                    <input
                                        value={formData.direccion}
                                        onChange={e => setFormData({...formData, direccion: e.target.value})}
                                        placeholder="Av. Principal 123, Ciudad"
                                    />
                                </div>

                                {/* Work Info */}
                                <div className="form-group">
                                    <label>Rol *</label>
                                    <select
                                        required
                                        value={formData.tipo_usuario_id}
                                        onChange={e => setFormData({...formData, tipo_usuario_id: e.target.value})}
                                        style={{
                                            width: '100%',
                                            padding: '0.625rem 0.875rem',
                                            borderRadius: 'var(--radius-md)',
                                            border: '1px solid var(--border-color)',
                                            background: 'var(--bg-input)',
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        <option value="">Seleccionar rol</option>
                                        {roles.map(r => (
                                            <option key={r.id} value={r.id}>{r.nombre}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Sucursal</label>
                                    <select
                                        value={formData.sucursal_id}
                                        onChange={e => setFormData({...formData, sucursal_id: e.target.value})}
                                        style={{
                                            width: '100%',
                                            padding: '0.625rem 0.875rem',
                                            borderRadius: 'var(--radius-md)',
                                            border: '1px solid var(--border-color)',
                                            background: 'var(--bg-input)',
                                            fontSize: '0.875rem'
                                        }}
                                    >
                                        <option value="">Seleccionar sucursal</option>
                                        {sucursales.map(s => (
                                            <option key={s.id} value={s.id}>{s.nombre}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Fecha de Contratación</label>
                                    <input
                                        type="date"
                                        value={formData.fecha_contratacion}
                                        onChange={e => setFormData({...formData, fecha_contratacion: e.target.value})}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Salario Base</label>
                                    <input
                                        type="number"
                                        value={formData.salario_base}
                                        onChange={e => setFormData({...formData, salario_base: e.target.value})}
                                        placeholder="500000"
                                    />
                                </div>

                                {editingId && (
                                    <div className="form-group">
                                        <label>Estado</label>
                                        <select
                                            value={formData.estado}
                                            onChange={e => setFormData({...formData, estado: e.target.value})}
                                            style={{
                                                width: '100%',
                                                padding: '0.625rem 0.875rem',
                                                borderRadius: 'var(--radius-md)',
                                                border: '1px solid var(--border-color)',
                                                background: 'var(--bg-input)',
                                                fontSize: '0.875rem'
                                            }}
                                        >
                                            <option value="activo">Activo</option>
                                            <option value="inactivo">Inactivo</option>
                                            <option value="vacaciones">Vacaciones</option>
                                            <option value="licencia">Licencia</option>
                                        </select>
                                    </div>
                                )}
                            </div>

                            <div className="form-actions" style={{ marginTop: '1.5rem' }}>
                                <button 
                                    type="button" 
                                    onClick={() => { setModalOpen(false); resetForm(); }}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--border-color)',
                                        background: 'white',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary">
                                    {editingId ? 'Actualizar' : 'Crear Colaborador'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Password Reset Modal */}
            {passwordModalOpen && selectedColabForPassword && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ maxWidth: '400px' }}>
                        <button 
                            className="modal-close" 
                            onClick={() => { setPasswordModalOpen(false); setSelectedColabForPassword(null); }}
                        >
                            <X size={20} />
                        </button>
                        
                        <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <Key size={24} />
                            Cambiar Contraseña
                        </h2>

                        <div style={{ 
                            background: '#f3f4f6', 
                            padding: '1rem', 
                            borderRadius: '8px', 
                            marginBottom: '1rem' 
                        }}>
                            <div style={{ fontWeight: '600' }}>{selectedColabForPassword.name}</div>
                            <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{selectedColabForPassword.email}</div>
                        </div>

                        <form onSubmit={handleResetPassword}>
                            <div className="form-group">
                                <label>Nueva Contraseña</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        value={newPassword}
                                        onChange={e => setNewPassword(e.target.value)}
                                        placeholder="Mínimo 6 caracteres"
                                        required
                                        minLength={6}
                                        style={{ paddingRight: '2.5rem' }}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        style={{
                                            position: 'absolute',
                                            right: '0.5rem',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: '#6b7280'
                                        }}
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={generateRandomPassword}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    marginBottom: '1rem',
                                    background: '#e0e7ff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    color: '#4f46e5',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem',
                                    fontWeight: '500'
                                }}
                            >
                                Generar contraseña aleatoria
                            </button>

                            <div style={{ 
                                background: '#fef3c7', 
                                padding: '0.75rem', 
                                borderRadius: '6px', 
                                marginBottom: '1rem',
                                fontSize: '0.75rem',
                                color: '#92400e'
                            }}>
                                <strong>Importante:</strong> Asegúrate de comunicar la nueva contraseña al colaborador de forma segura.
                            </div>

                            <div className="form-actions">
                                <button 
                                    type="button" 
                                    onClick={() => { setPasswordModalOpen(false); setSelectedColabForPassword(null); }}
                                    style={{
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: 'var(--radius-md)',
                                        border: '1px solid var(--border-color)',
                                        background: 'white',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-primary">
                                    Cambiar Contraseña
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
